import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Download, Eye, Search, AlertCircle, Lock } from 'lucide-react';
import { authStorage } from '../../utils/storage';

// Read-only reporting hub for any signed-in user. Backed by the verifyToken-only
// /api/reports/* endpoints (not the admin-gated /api/admin/reports/*), so the
// sensitive reports (audit log, login history, user accounts) are never here.
// Non-admins are server-scoped to their own assigned units; admins can filter
// by any employee (or all).
const SECTIONS = [
  {
    title: 'Officers & Leadership',
    reports: [
      { key: 'officer-coverage', label: 'Officer Coverage', desc: 'Every unit with President / VP / Secretary / Treasurer coverage and a Gap flag (No Officers / No President), gaps sorted to the top.' },
      { key: 'officer-roster', label: 'Officer Roster', desc: 'Current officers by unit, with email.' },
      { key: 'terms-elections', label: 'Terms & Elections', desc: 'Election dates, term length, and term-expired status by unit.' },
    ],
  },
  {
    title: 'Contracts & Raises',
    reports: [
      { key: 'contracts-expiring', label: 'Contracts Expiring', desc: 'Units whose contract expires within ~18 months (or already expired). A negative DAYS_LEFT means it has already expired.' },
      { key: 'contracts-missing', label: 'Missing Contract Dates', desc: 'Units with no real CBA start / expiration on file.' },
      { key: 'raises', label: 'Wage Raises / GWI', desc: 'Recorded raise history by unit and effective date.' },
    ],
  },
  {
    title: 'Membership & Dues',
    reports: [
      { key: 'membership-summary', label: 'Membership & Dues', desc: 'Latest audit per local: total members, paying dues, % paying, opt-outs, and refusals.' },
    ],
  },
  {
    title: 'Units & Assignments',
    reports: [
      { key: 'unit-directory', label: 'Unit Directory', desc: 'All active units with rep, town, status, and contract dates.' },
      { key: 'unassigned-units', label: 'Unassigned Units', desc: 'Active units with no staff rep assigned.' },
      { key: 'decertified', label: 'Decertified Units', desc: 'Units flagged as decertified.' },
    ],
  },
];

const ALL = SECTIONS.flatMap((s) => s.reports);

export default function ReportsView() {
  const [active, setActive] = useState(null);
  const [rows, setRows] = useState([]);
  const [cols, setCols] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState('');
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [hideDecert, setHideDecert] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [employee, setEmployee] = useState('all'); // admin-only selector
  const [counts, setCounts] = useState(null); // per-report row counts for the current scope (null = no scope = nothing blocked)
  const token = authStorage.getToken();
  const role = authStorage.getRole() || 'User';
  const username = authStorage.getUsername() || '';
  // Research/membership office staff who work org-wide data. Mirrors the server's
  // canSeeAllReports (minus the coordinator DB lookup). `isAdmin` here means
  // "org-wide report access": sees all units + the employee filter rather than
  // being scoped to their own assignments.
  const RESEARCH_DEPT_USERS = ['mbaran', 'nsewell', 'MMcCalla'];
  const isAdmin = ['Admin', 'Manager', 'admin', 'manager', 'Membership', 'membership'].includes(role) || RESEARCH_DEPT_USERS.includes(username);

  // The employee scope is a server query param, so it becomes part of each fetch.
  const scopeParam = (isAdmin && employee !== 'all') ? `employee=${encodeURIComponent(employee)}` : '';

  // Admins get the employee list for the dropdown. Non-admins are locked to
  // their own assignments (the server enforces it) and get no picker.
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const res = await fetch('/api/reports/employees', { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const data = await res.json();
        setEmployees(Array.isArray(data.employees) ? data.employees : []);
      } catch (e) { /* dropdown just stays at "All employees" */ }
    })();
  }, [isAdmin, token]);

  const view = useCallback(async (key) => {
    setActive(key); setLoading(true); setError(''); setRows([]); setCols([]);
    try {
      const qs = scopeParam ? `?${scopeParam}` : '';
      const res = await fetch(`/api/reports/${key}${qs}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const r = Array.isArray(data) ? data : (data.rows || []);
      const c = Array.isArray(data.headers) && data.headers.length ? data.headers.slice() : (r[0] ? Object.keys(r[0]) : []);
      setRows(r); setCols(c);
    } catch (e) {
      setError('Could not load this report. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [scopeParam, token]);

  // When the scope changes, pull per-report counts so we can grey out reports
  // that have nothing for the selected employee, and re-run (or close) the open
  // report. Admin viewing "All employees" has no scope → nothing is blocked.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const scoped = !isAdmin || employee !== 'all';
      if (!scoped) {
        setCounts(null);
        if (active) view(active);
        return;
      }
      let cts = null;
      try {
        const qs = (isAdmin && employee !== 'all') ? `?employee=${encodeURIComponent(employee)}` : '';
        const res = await fetch(`/api/reports/summary${qs}`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) cts = (await res.json()).counts || {};
      } catch (e) { /* leave counts null → nothing blocked */ }
      if (cancelled) return;
      setCounts(cts);
      if (active) {
        if (cts && cts[active] === 0) setActive(null); // open report is now empty for this scope — close it
        else view(active);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee, isAdmin]);

  const download = async (rpt) => {
    setDownloading(rpt.key); setError('');
    try {
      const qs = `format=csv${scopeParam ? `&${scopeParam}` : ''}`;
      const res = await fetch(`/api/reports/${rpt.key}?${qs}`, {
        headers: { Authorization: `Bearer ${token}`, Accept: 'text/csv' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${rpt.key}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError('Could not download the CSV. Please try again.');
    } finally {
      setDownloading('');
    }
  };

  const activeLabel = ALL.find((r) => r.key === active)?.label;
  const hasStatus = cols.includes('STATUS');
  const f = filter.trim().toLowerCase();
  const visibleRows = rows.filter((r) => {
    if (hideDecert && hasStatus && String(r.STATUS) === 'Decertified') return false;
    if (f) return cols.some((c) => String(r[c] == null ? '' : r[c]).toLowerCase().includes(f));
    return true;
  });

  return (
    <div className="unit-detail-page" style={{ padding: 16 }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, margin: '0 0 4px', letterSpacing: '-0.01em' }}>Reports</h1>
      <p style={{ color: 'var(--fg-2)', margin: '0 0 14px' }}>
        Pull the data you need out of Datahouse — view it on screen (filter &amp; sort), or download to Excel (CSV).
      </p>

      {/* Employee scope: dropdown for admins, a locked note for everyone else. */}
      <div className="card" style={{ padding: '10px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {isAdmin ? (
          <>
            <label htmlFor="rpt-emp" style={{ fontWeight: 600, fontSize: 13 }}>Employee</label>
            <select id="rpt-emp" value={employee} onChange={(e) => setEmployee(e.target.value)}
              style={{ padding: '6px 10px', fontSize: 13, borderRadius: 6, border: '1px solid var(--border, #e5e7eb)', minWidth: 220, background: 'var(--bg-surface, #fff)' }}>
              <option value="all">All employees</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
            <span style={{ color: 'var(--fg-3)', fontSize: 12 }}>Reports show units assigned to the selected employee (primary or secondary rep).</span>
          </>
        ) : (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--fg-2)', fontSize: 13 }}>
            <Lock size={14} /> Showing only the units assigned to you.
          </span>
        )}
      </div>

      {error && (
        <div className="card" style={{ padding: 10, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--color-danger, #dc2626)', fontWeight: 600 }}>
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {SECTIONS.map((section) => (
        <div key={section.title} style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--fg-3)', margin: '0 0 8px' }}>{section.title}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
            {section.reports.map((rpt) => {
              const blocked = counts && counts[rpt.key] === 0;
              return (
                <div key={rpt.key} className="card" style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 8, opacity: blocked ? 0.55 : 1, outline: active === rpt.key ? '2px solid var(--accent, #2563eb)' : 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}><FileText size={17} />{rpt.label}</div>
                  <div style={{ fontSize: 13, color: 'var(--fg-2)', flex: 1 }}>{rpt.desc}</div>
                  {blocked ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--fg-3)', fontStyle: 'italic' }}>
                      <Lock size={13} /> {isAdmin ? 'Nothing for this employee' : 'Nothing assigned to you'}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => view(rpt.key)}><Eye size={14} /> View</button>
                      <button className="btn btn-primary btn-sm" onClick={() => download(rpt)} disabled={downloading === rpt.key}>
                        <Download size={14} /> {downloading === rpt.key ? 'Downloading…' : 'CSV'}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {active && (
        <div className="card" style={{ padding: 14, marginTop: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
            <strong>{activeLabel}</strong>
            {loading
              ? <span style={{ color: 'var(--fg-2)', fontSize: 13 }}>Loading…</span>
              : <span style={{ color: 'var(--fg-2)', fontSize: 13 }}>{visibleRows.length}{visibleRows.length !== rows.length ? ` of ${rows.length}` : ''} row{rows.length === 1 ? '' : 's'}</span>}
            <div style={{ flex: 1 }} />
            {!loading && rows.length > 0 && (
              <>
                <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                  <Search size={14} style={{ position: 'absolute', left: 8, color: 'var(--fg-3)' }} />
                  <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter…"
                    style={{ padding: '5px 8px 5px 28px', fontSize: 13, borderRadius: 6, border: '1px solid var(--border, #e5e7eb)', minWidth: 180 }} />
                </span>
                {hasStatus && (
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--fg-2)' }}>
                    <input type="checkbox" checked={hideDecert} onChange={(e) => setHideDecert(e.target.checked)} /> Hide decertified
                  </label>
                )}
              </>
            )}
          </div>
          {!loading && rows.length > 0 && (
            <div style={{ overflowX: 'auto', maxHeight: 560 }}>
              <table className="tbl" style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                <thead>
                  <tr>{cols.map((c) => (
                    <th key={c} style={{ textAlign: 'left', padding: '6px 10px', position: 'sticky', top: 0, background: 'var(--bg-1, #f8fafc)', whiteSpace: 'nowrap' }}>{c}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {visibleRows.slice(0, 1000).map((r, i) => (
                    <tr key={i}>{cols.map((c) => (
                      <td key={c} style={{ padding: '5px 10px', borderTop: '1px solid var(--border, #e5e7eb)', whiteSpace: 'nowrap' }}>{r[c] == null ? '' : String(r[c])}</td>
                    ))}</tr>
                  ))}
                </tbody>
              </table>
              {visibleRows.length > 1000 && (
                <div style={{ color: 'var(--fg-2)', fontSize: 12, marginTop: 8 }}>Showing the first 1,000 rows — download the CSV for the full list.</div>
              )}
            </div>
          )}
          {!loading && rows.length === 0 && !error && <div style={{ color: 'var(--fg-2)' }}>No rows found.</div>}
        </div>
      )}
    </div>
  );
}
